<template>
  <div class="search-container">
    <!-- 顶部导航栏 -->
    <div class="search-navbar">
      <img src="@/assets/back.png" class="nav-icon back-icon" @click="goBack" />
      <div class="search-input-wrap">
        <img src="@/assets/search-gray.png" class="input-icon" />
        <input
          v-model="keyword"
          class="search-input"
          :placeholder="hotList.length > 0 ? hotList[0].name : t('search.btn')"
        />
      </div>
      <button class="search-btn" @click="onSearch">
        {{ t("search.btn") }}
      </button>
    </div>

    <!-- 历史搜索列表 -->
    <div
      class="history-section"
      v-if="!searchStore.hasSearched && searchStore.searchHistory.length"
    >
      <div class="history-title-row">
        <div class="history-title">{{ t("search.historyTitle") }}</div>
        <img
          src="@/assets/delete.png"
          class="delete-icon"
          @click="handleClearHistory"
        />
      </div>
      <div class="history-list">
        <div
          v-for="(item, index) in searchStore.searchHistory"
          :key="index"
          class="history-item"
          @click="handleHistoryItemClick(item)"
        >
          <span class="history-name">{{ item }}</span>
        </div>
      </div>
    </div>

    <!-- 热搜列表 -->
    <div class="hot-section" v-if="!searchStore.hasSearched && hotList.length">
      <div class="hot-title">{{ t("search.hotTitle") }}</div>
      <div v-if="hotLoading" class="hot-loading">
        <Loading />
      </div>
      <div class="hot-list">
        <div
          v-for="(item, idx) in hotList"
          :key="item.id"
          class="hot-item"
          @click="handleHotItemClick(item)"
        >
          <span class="hot-index" :class="{ hot: item.hot }"
            >{{ idx + 1 }}.</span
          >
          <span class="hot-name">{{ item.name }}</span>
          <img v-if="item.hot" src="@/assets/hot.png" class="hot-icon" />
        </div>
      </div>
    </div>

    <!-- 搜索结果区 -->
    <SearchResult v-if="searchStore.hasSearched" :keyword="keyword" />

    <!-- 全屏 Loading -->
    <div
      v-if="searchStore.hasSearched && searchStore.currentLoading"
      class="fullscreen-loading"
    >
      <Loading />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, onActivated } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useSearchStore } from "@/stores/search";
import Loading from "@/components/common/Loading.vue";
import SearchResult from "@/components/search/SearchResult.vue";
import { clickButton, pageType } from "@/utils/point";
import { useToastStore } from "@/stores";

const { t } = useI18n();
const router = useRouter();
const searchStore = useSearchStore();
const toast = useToastStore();
const keyword = ref("");
const loading = ref(false);
const hotLoading = ref(false);
const hotList = ref<any[]>([]);

onMounted(() => {
  // 首次加载时也获取热词数据和历史数据
  initHotKeywords();
  initHistoryData();
});

onActivated(() => {
  // 获取热词数据
  initHotKeywords();
  // 初始化历史搜索数据
  initHistoryData();
});

// 初始化热词数据
const initHotKeywords = async () => {
  hotLoading.value = true;
  await searchStore.getHotKeywords();
  // 将热词数据转换为组件需要的格式
  hotList.value = searchStore.hotKeywords.map((keyword, index) => ({
    id: index + 1,
    name: keyword.keyword || keyword,
    hot: index < 3, // 前3个标记为热门
  }));
  hotLoading.value = false;
};

// 初始化历史搜索数据
const initHistoryData = () => {
  // 从 store 加载搜索历史
  searchStore.loadSearchHistory();
};

onUnmounted(() => {
  // 清理热词数据
  hotList.value = [];
  hotLoading.value = false;
  // 重置搜索状态
  searchStore.resetAll();
});

const goBack = () => {
  if (searchStore.hasSearched) {
    searchStore.setHasSearched(false);
  } else {
    router.back();
  }
};

const onSearch = async () => {
  if (!keyword.value && hotList.value.length > 0) {
    keyword.value = hotList.value[0].name;
  }
  // 添加搜索历史
  if (keyword.value.trim()) {
    searchStore.addSearchHistory(keyword.value);
  }
  searchStore.clearSearchList();
  searchStore.setHasSearched(true);
  const res = await searchStore.searchCurrentTab(keyword.value);
  if (res.code != 200) {
    toast.showErrorCodeToast(+res.code, t);
  }
  clickButton({
    eventType: pageType.SEARCH,
    eventReason: "触发搜索",
    eventValue: JSON.stringify({
      keyword: keyword.value,
    }),
  });
};
const handleHotItemClick = (item: any) => {
  keyword.value = item.name;
  // 添加搜索历史
  searchStore.addSearchHistory(item.name);
  clickButton({
    eventType: pageType.SEARCH,
    eventReason: "点击搜索热词",
    eventValue: JSON.stringify({
      keyword: item.name,
    }),
  });
  onSearch();
};
const handleClearHistory = () => {
  clickButton({
    eventType: pageType.SEARCH,
    eventReason: "清空搜索历史",
  });
  searchStore.clearSearchHistory();
};
const handleHistoryItemClick = (item: string) => {
  keyword.value = item;
  clickButton({
    eventType: pageType.SEARCH,
    eventReason: "点击搜索历史",
    eventValue: JSON.stringify({
      keyword: item,
    }),
  });
  onSearch();
};
</script>

<style scoped lang="scss">
.search-container {
  background: #f4f6fa;
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  padding: 0 calc((100% - 800px) / 2) 50px;
  overflow-x: hidden;
  box-sizing: border-box;
}
.search-navbar {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}
.nav-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
}
.search-input-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  background: #f0f2f5;
  border-radius: 24px;
  margin: 0 8px;
  padding: 0 12px;
  transition: all 0.3s;
}
.input-icon {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  opacity: 0.6;
  flex-shrink: 0;
}
.search-input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 15px;
  flex: 1;
  min-width: 0;
  padding: 10px 10px 10px 0;
  color: #1a1a1a;
  &::placeholder {
    color: #999;
  }
}
.search-btn {
  background: linear-gradient(135deg, #ffd400 0%, #ffb800 100%);
  color: #1a1a1a;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: 8px;
}
.hot-section {
  margin: 28px 0 0 0;
  padding: 0 24px;
}
.hot-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #1a1a1a;
  position: relative;
}
.hot-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
}

.hot-empty {
  text-align: center;
  color: #999;
  padding: 20px 0;
  font-size: 14px;
}

.hot-list {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
}
.hot-item {
  display: flex;
  align-items: center;
  font-size: 15px;
  color: #4a4a4a;
  transition: all 0.2s;
  cursor: pointer;
  &:hover {
    color: #1a1a1a;
    transform: translateX(2px);
  }
}
.hot-index {
  color: #ff9500;
  font-weight: 600;
  margin-right: 8px;
  font-size: 16px;
  &.hot {
    color: #ff3b30;
  }
}
.hot-name {
  margin-right: 5px;
}
.hot-icon {
  width: 12px;
  height: 12px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.empty-tip {
  text-align: center;
  color: #999;
  margin: 40px 0;
  font-size: 16px;
}

.search-tabs {
  display: flex;
  border-bottom: 1px solid #eee;
  margin-bottom: 5px;
  span {
    flex: 1;
    text-align: center;
    padding: 12px 0;
    font-size: 16px;
    color: #999;
    background: #fff;

    cursor: pointer;
    &.active {
      color: #222;
      font-weight: bold;
      border-bottom: 2px solid #dd8632;
    }
  }
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  gap: 16px;
  max-width: 800px;
  margin: 0 auto;
  font-size: 14px;
  .related-title {
    color: #444;
    font-weight: 500;
  }

  .filter-options {
    display: flex;
    gap: 16px;
  }

  span {
    color: #666;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      color: #333;
    }

    &.active {
      color: #dd8632;
      font-weight: 600;
    }
  }
}

.post-list {
  padding: 0 12px;
}

.history-section {
  margin: 28px 0 0 0;
  padding: 0 24px;
}

.history-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.history-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
}

.delete-icon {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.history-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.history-item {
  display: flex;
  align-items: center;
  font-size: 15px;
  color: #999;
  transition: all 0.2s;
  cursor: pointer;
  background: #e9e9e9;
  border-radius: 16px;
  padding: 6px 18px;
  &:hover {
    color: #666;
  }
}

.history-name {
  margin-right: 5px;
}

.load-more-trigger {
  height: 1px;
  opacity: 0;
}

.fullscreen-loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}
</style>
